import { useEffect, useState } from 'react'
import { Form, Formik } from 'formik'
import { Icon } from '@iconify/react'
import { useHistory } from 'react-router-dom'
import {
  BooleanParam,
  NumberParam,
  useQueryParams,
  withDefault,
} from 'use-query-params'
import { Button, RadioGroup, TextField } from '@pepper/ui'

import { validationSchema } from './schema'

import s from './PositionFilter.module.css'

const options = [
  { value: 'all', label: 'Exclude all' },
  { value: '1-10', label: 'Positions 1 - 10' },
  { value: '1-20', label: 'Positions 1 - 20' },
  { value: '1-30', label: 'Positions 1 - 30' },
  { value: 'custom', label: 'Custom' },
]

export const PositionFilter = () => {
  const history = useHistory()
  const [queryParams, setQueryParams] = useQueryParams({
    positionLte: withDefault(NumberParam, undefined),
    positionGte: withDefault(NumberParam, undefined),
    sug_positionLte: withDefault(NumberParam, undefined),
    sug_positionGte: withDefault(NumberParam, undefined),
    page: withDefault(NumberParam, 1),
    exclude_position: withDefault(BooleanParam, false),
    sug_exclude_position: withDefault(BooleanParam, false),
    content_ideas: withDefault(BooleanParam, false),
  })

  const [selectedOption, setSelectedOption] = useState<string>()
  const isSuggestionsFilter =
    history.location.pathname === '/content-ideas' || queryParams.content_ideas

  const disabled = selectedOption !== 'custom'

  const lteKey = isSuggestionsFilter ? 'sug_positionLte' : 'positionLte'
  const gteKey = isSuggestionsFilter ? 'sug_positionGte' : 'positionGte'
  const excludePositionKey = isSuggestionsFilter
    ? 'sug_exclude_position'
    : 'exclude_position'

  const handleChange = (value: string) => {
    setSelectedOption(value)
    const lte = Number(value.split('-')?.[1])
    const gte = Number(value.split('-')?.[0])

    if (value !== 'custom' && value !== 'all') {
      setQueryParams({
        [gteKey]: gte,
        [lteKey]: lte,
        [excludePositionKey]: true,
      })
    } else {
      setQueryParams({
        [gteKey]: undefined,
        [lteKey]: undefined,
        [excludePositionKey]: true,
      })
    }
    setQueryParams({
      page: 1,
    })
  }

  const handleSubmit = (to: number, from: number) => {
    setQueryParams({
      [gteKey]: from,
      [lteKey]: to,
      page: 1,
    })
  }

  const getSelectedOption = () => {
    if (queryParams[gteKey]) {
      return (
        options.find(option => {
          const splitValue = option.value.split('-')
          return (
            Number(splitValue[0]) === queryParams[gteKey] &&
            Number(splitValue[1]) === queryParams[lteKey]
          )
        })?.value ?? 'custom'
      )
    }
    return selectedOption
  }

  useEffect(() => {
    if (!queryParams[excludePositionKey] && !!selectedOption) {
      setSelectedOption(undefined)
    }
  }, [queryParams.exclude_position, queryParams.sug_exclude_position])

  return (
    <div className={s.wrapper}>
      <div className={s.title}>Filter by excluding</div>
      <div className={s.customWrapper}>
        <Formik
          initialValues={{
            from: disabled ? '' : `${queryParams[gteKey] ?? ''}`,
            to: disabled ? '' : `${queryParams[lteKey] ?? ''}`,
          }}
          onSubmit={value => handleSubmit(Number(value.to), Number(value.from))}
          validationSchema={validationSchema}
          enableReinitialize={true}
        >
          {({ errors, resetForm, dirty }) => (
            <Form>
              <div className={s.optionsWrapper}>
                <RadioGroup
                  value={getSelectedOption()}
                  options={options}
                  className={s.radioGroup}
                  onChange={e => {
                    if (e.target.value !== 'custom') {
                      resetForm()
                    }
                    handleChange(e.target.value)
                  }}
                />
              </div>
              <div className={s.inputWrapper}>
                <TextField
                  fieldProps={{
                    placeholder: 'From',
                    disabled,
                  }}
                  showError={false}
                  className={s.rangeInput}
                  name="from"
                />
                <TextField
                  fieldProps={{
                    placeholder: 'To',
                    disabled,
                  }}
                  className={s.rangeInput}
                  showError={false}
                  name="to"
                />
              </div>
              {(errors.from || errors.to) && !disabled && (
                <div className={s.error}>
                  <Icon
                    icon="ant-design:close-circle-filled"
                    className={s.errorIcon}
                  />
                  {errors.from || errors.to}
                </div>
              )}
              <Button
                type="primary"
                htmlType="submit"
                disabled={!!errors.from || !!errors.to || disabled || !dirty}
                size="large"
              >
                Submit
              </Button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  )
}
